import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Snackbar } from '../../components/Snackbar';
import ContentSection from '../SendMessageForAll/ContentSection';
import MessagePreview from '../SendMessageForAll/MessagePreview';
import {
  adCodeStrings,
  messageSettingsStrings,
} from '../../constants/strings';
import { queryKeys } from '../../constants/queryKeys';
import {
  getMessageTemplates,
  upsertMessageTemplate,
  validateGreetingChatVideos,
  TMessageTemplateType,
} from '../../api/messageTemplates';
import { getAutoReplyMessageAssets } from '../../api/autoReply';
import {
  useOrganizationId,
  useUnsavedChangesGuard,
} from '../../components/OrganizationScope/context';
import useUploadFile from '../../hooks/useUploadFile';
import useToast from '../../hooks/useToast';
import { useMessageSections } from '../../hooks/useMessageSections';
import {
  formatSectionsForAPI,
  convertDraftSectionsToMessageSections,
} from '../SendMessageForAll/utils';
import {
  areMessageAssetSectionsValid,
  areSurveySectionsValid,
  isSectionFilled,
} from 'utils/messageSection';
import { EContentSectionType, EOrganizationPlan } from '../../types';
import { useState } from 'react';
import { useCurrentUser } from 'hooks';
import { isInternalOrganizationPlan } from 'utils/organizationPlan';
import { MessageAssetQueryError } from '../../components/MessageAssetQueryError';
import { sendErrorMessages } from '../../constants/errorStrings';
import { MAX_LINE_VIDEO_SIZE } from '../../constants';
import { MAX_CHAT_VIDEO_SIZE } from '../../components/Chat/constants';
import { pageCardClasses } from '../../components/AppLayout/styles';

const TEMPLATE_TYPES: TMessageTemplateType[] = [
  'NOTIFICATION',
  'GREETING',
  'MIGRATION',
];
const MAX_SECTIONS = 5;
const BUTTON_LABEL_MAX_LENGTH = 20;

function getDefaultButtonLabel(type: TMessageTemplateType): string {
  return messageSettingsStrings.buttonLabels[type];
}

export default function MessageSettings() {
  const [activeTab, setActiveTab] =
    useState<TMessageTemplateType>('NOTIFICATION');
  // 挨拶(GREETING)を個別チャットにも自動送信するか（GREETINGタブでのみ使用）
  const [sendToChat, setSendToChat] = useState(false);
  // アンケート招待画像のアップロード完了前に保存するとimageUrl未設定で確定するため進行中は保存を止める
  const [uploadingSurveySectionIds, setUploadingSurveySectionIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const handleSurveyUploadingChange = useCallback(
    (sectionId: string, uploading: boolean) => {
      setUploadingSurveySectionIds((current) => {
        if (current.has(sectionId) === uploading) return current;
        const next = new Set(current);
        if (uploading) {
          next.add(sectionId);
        } else {
          next.delete(sectionId);
        }
        return next;
      });
    },
    [],
  );
  const isSurveyImageUploading = uploadingSurveySectionIds.size > 0;
  const [buttonLabel, setButtonLabel] = useState(
    getDefaultButtonLabel('NOTIFICATION'),
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    variant: 'success' | 'error';
  }>({
    open: false,
    message: '',
    variant: 'success',
  });
  // タブごとに1回だけサーバー値を読み込むためのフラグ。
  // templates の再フェッチ(ウィンドウ再フォーカス/保存後の invalidate)で
  // 未保存の編集(トグル・本文)が上書きされるのを防ぐ。
  const hydratedTabRef = useRef<TMessageTemplateType | null>(null);

  const { data: currentUser } = useCurrentUser();
  const isLightPlan = currentUser?.organizationPlan === EOrganizationPlan.LIGHT;
  const isInternalPlan = isInternalOrganizationPlan(
    currentUser?.organizationPlan,
  );

  // プラン別に表示するタブを決定する。
  // 通知=チャット着信通知のためLIGHTでは非表示、移行=自社利用(INTERNAL)のみ、挨拶=常に表示。
  const visibleTabs = useMemo(
    () =>
      TEMPLATE_TYPES.filter((type) => {
        if (type === 'NOTIFICATION') return !isLightPlan;
        if (type === 'MIGRATION') return isInternalPlan;
        return true;
      }),
    [isLightPlan, isInternalPlan],
  );

  const {
    sections,
    setSections,
    handleAddSection,
    handlePatchSection,
    handleChangeType,
    handleDeleteSection,
    handleMoveSection,
    handleUpdateSection,
  } = useMessageSections();

  const queryClient = useQueryClient();
  const { uploadToS3 } = useUploadFile();
  // アップロードの後に保存するため、送信先の組織を操作開始時に固定する
  const organizationId = useOrganizationId();
  const { toastError } = useToast();

  const { data: templates } = useQuery({
    queryKey: [queryKeys.MESSAGE_TEMPLATES],
    queryFn: getMessageTemplates,
  });
  const {
    data: cardAssets = [],
    isSuccess: cardAssetsReady,
    isError: cardAssetsLoadError,
    isFetching: isFetchingCardAssets,
    refetch: refetchCardAssets,
  } = useQuery({
    queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS, 'CARD_TYPE_MESSAGE'],
    queryFn: () => getAutoReplyMessageAssets('CARD_TYPE_MESSAGE'),
  });

  const greetingCardAssets =
    activeTab === 'GREETING' ? cardAssets : [];

  const hasTextSection = sections.some(
    (s) => s.type === EContentSectionType.TEXT && isSectionFilled(s),
  );
  const hasGreetingCardSection =
    activeTab === 'GREETING' &&
    sections.some(
      (section) =>
        section.type === EContentSectionType.CARD_TYPE_MESSAGE,
    );
  const hasSurveySection =
    activeTab === 'GREETING' &&
    sections.some(
      (section) =>
        section.type === EContentSectionType.SURVEY && isSectionFilled(section),
    );
  // 流入元計測(#403)はあいさつのメディアを計測点に使うため、
  // 画像・動画・カードのいずれも無いと確定できない旨を伝える。
  // セクションを置いただけ（画像未アップロード等）では計測点にならないので、
  // 中身が入っているものだけを数える。
  const hasGreetingMedia =
    activeTab === 'GREETING' &&
    sections.some(
      (section) =>
        (section.type === EContentSectionType.IMAGE ||
          section.type === EContentSectionType.VIDEO ||
          section.type === EContentSectionType.CARD_TYPE_MESSAGE) &&
        isSectionFilled(section),
    );
  const showAdCodeMediaNotice =
    activeTab === 'GREETING' && !hasGreetingMedia;

  const hasValidGreetingAssets = areMessageAssetSectionsValid(
    sections,
    greetingCardAssets,
    cardAssetsReady,
  );
  const hasRequiredSection =
    (hasTextSection || hasGreetingCardSection || hasSurveySection) &&
    hasValidGreetingAssets;
  const effectiveSendToChat = sendToChat && !hasGreetingCardSection;
  const allowFileSection =
    activeTab === 'GREETING' && !isLightPlan && effectiveSendToChat;
  // 挨拶を個別チャットにも送るときだけ、動画は個別チャット用の上限（100MB）で扱う。
  // アンケート混在時は backend が sendToChat を false で保存するためチャットには送られない。
  const isGreetingChatVideoLimit =
    activeTab === 'GREETING' && !isLightPlan && effectiveSendToChat && !hasSurveySection;
  const maxVideoBytes = isGreetingChatVideoLimit
    ? MAX_CHAT_VIDEO_SIZE
    : MAX_LINE_VIDEO_SIZE;

  const trimmedButtonLabel = buttonLabel.trim();
  // ライトプランは導線ボタン自体が無いため、ラベルのバリデーションを免除する。
  const markTemplateSaved = useUnsavedChangesGuard(
    JSON.stringify({ sections, effectiveSendToChat, buttonLabel }),
    isSurveyImageUploading,
  );
  const isButtonLabelValid =
    isLightPlan ||
    (trimmedButtonLabel.length > 0 &&
      trimmedButtonLabel.length <= BUTTON_LABEL_MAX_LENGTH);

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      if (!hasValidGreetingAssets) {
        throw new Error(sendErrorMessages.invalidAssetSelection);
      }
      if (!areSurveySectionsValid(sections)) {
        throw new Error(sendErrorMessages.surveySelectionRequired);
      }
      if (!hasRequiredSection) {
        throw new Error(
          activeTab === 'GREETING'
            ? messageSettingsStrings.greetingContentRequired
            : messageSettingsStrings.textSectionRequired,
        );
      }
      if (!isButtonLabelValid) {
        throw new Error(
          trimmedButtonLabel.length === 0
            ? messageSettingsStrings.buttonLabelRequired
            : messageSettingsStrings.buttonLabelTooLong,
        );
      }
      // PDF(FILE)は「挨拶 + 個別チャットにも送信ON」のときだけ配信経路がある。
      // サーバ側でも拒否されるが、S3アップロードが走る前に弾いて孤児ファイルを防ぐ
      if (
        !allowFileSection &&
        sections.some(
          (s) => s.type === EContentSectionType.FILE && isSectionFilled(s),
        )
      ) {
        throw new Error(messageSettingsStrings.fileSectionGreetingOnly);
      }
      const formatted = await formatSectionsForAPI(sections, {
        uploadToS3,
        onError: toastError,
        maxVideoBytes,
        validateSavedVideos: isGreetingChatVideoLimit
          ? validateGreetingChatVideos
          : undefined,
      });
      if (formatted.length === 0)
        throw new Error(messageSettingsStrings.saveFailed);
      return upsertMessageTemplate(
        activeTab,
        formatted,
        activeTab !== 'GREETING'
          ? undefined
          : hasGreetingCardSection
            ? false
            : isLightPlan
              ? undefined
              : effectiveSendToChat,
        isLightPlan ? undefined : trimmedButtonLabel,
        organizationId,
      );
    },
    onSuccess: () => {
      // 保存成功時はガードを解除し、再フェッチでサーバー正規化値(アップロード後のS3 URL等)へ
      // 再同期する。これを忘れると新規画像のローカル file 参照が残り、再保存で再アップロードされる。
      hydratedTabRef.current = null;
      markTemplateSaved();
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MESSAGE_TEMPLATES],
      });
      setSnackbar({
        open: true,
        message: messageSettingsStrings.saveSuccess,
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message || messageSettingsStrings.saveFailed,
        variant: 'error',
      });
    },
  });

  // 非同期取得の currentUser 確定後、選択中タブが非表示になった場合は先頭タブへ戻す。
  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) {
      hydratedTabRef.current = null;
      setActiveTab(visibleTabs[0] ?? 'GREETING');
    }
  }, [visibleTabs, activeTab]);

  useEffect(() => {
    if (!templates) return;
    // このタブは既に hydrate 済み → 再フェッチで未保存の編集を上書きしない
    if (hydratedTabRef.current === activeTab) return;
    hydratedTabRef.current = activeTab;
    const template = templates.find((t) => t.type === activeTab);
    setSendToChat(
      activeTab === 'GREETING' ? (template?.sendToChat ?? false) : false,
    );
    if (template && template.sections.length > 0) {
      setSections(convertDraftSectionsToMessageSections(template.sections));
    } else {
      const defaultContent = isLightPlan
        ? messageSettingsStrings.planDefaults[activeTab]
        : messageSettingsStrings.defaults[activeTab];
      setSections([
        {
          id: Date.now().toString(),
          type: EContentSectionType.TEXT,
          content: defaultContent,
        },
      ]);
    }
    setButtonLabel(
      template?.buttonLabel?.trim() || getDefaultButtonLabel(activeTab),
    );
    markTemplateSaved();
  }, [activeTab, templates, setSections, isLightPlan, markTemplateSaved]);

  // カードは個別チャットへ同期しない。UI表示・保存値だけでなく、カード削除後も
  // 過去のONへ自動復帰しないようstate自体をOFFへ正規化する。
  useEffect(() => {
    if (hasGreetingCardSection) setSendToChat(false);
  }, [hasGreetingCardSection]);

  return (
    <>
      <section className={`w-full h-auto lg:h-[calc(100vh-12rem)] flex flex-col lg:overflow-hidden ${pageCardClasses.regular}`}>
        <header className="flex-shrink-0 flex items-center gap-4 mb-6">
          <h1 className="text-2xl font-display font-bold text-ink">
            {messageSettingsStrings.title}
          </h1>
        </header>

        <div className="flex flex-col gap-6 flex-1 min-h-0 lg:flex-row lg:gap-10">
          <div className="flex-1 overflow-y-auto overflow-x-hidden pr-2">
            <div className="flex flex-col gap-4">
              {showAdCodeMediaNotice && (
                <div
                  className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  role="status"
                >
                  {adCodeStrings.greetingWarning}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4">
                <div
                  className="flex gap-1 border-b border-border/70"
                  role="tablist"
                >
                  {visibleTabs.map((type) => (
                    <button
                      key={type}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === type}
                      onClick={() => {
                        if (type !== activeTab) hydratedTabRef.current = null;
                        setActiveTab(type);
                      }}
                      className={`px-4 py-2 text-xs font-semibold transition border-b-2 -mb-px ${
                        activeTab === type
                          ? 'border-accent text-accent'
                          : 'border-transparent text-muted hover:text-ink'
                      }`}
                    >
                      {messageSettingsStrings.tabs[type]}
                    </button>
                  ))}
                </div>
                <span className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                  {messageSettingsStrings.descriptions[activeTab]}
                </span>
              </div>

              {activeTab === 'GREETING' && cardAssetsLoadError ? (
                <MessageAssetQueryError
                  isRetrying={isFetchingCardAssets}
                  onRetry={() => {
                    void refetchCardAssets();
                  }}
                />
              ) : null}

              {activeTab === 'GREETING' && !isLightPlan && (
                <label className="flex items-start gap-3 rounded-xl border border-border/70 bg-white/70 p-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={effectiveSendToChat}
                    aria-label={messageSettingsStrings.sendToChatLabel}
                    onClick={() => setSendToChat((v) => !v)}
                    disabled={hasGreetingCardSection}
                    className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${
                      effectiveSendToChat ? 'bg-accent' : 'bg-neutral-300'
                    } disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
                        effectiveSendToChat
                          ? 'translate-x-4'
                          : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <span className="grid gap-0.5">
                    <span className="text-sm font-semibold text-ink">
                      {messageSettingsStrings.sendToChatLabel}
                    </span>
                    <span className="text-xs text-muted">
                      {hasGreetingCardSection
                        ? messageSettingsStrings.sendToChatCardDisabledHint
                        : messageSettingsStrings.sendToChatHint}
                    </span>
                  </span>
                </label>
              )}

              {!isLightPlan && (
                <label className="grid max-w-md gap-1.5">
                  <span className="text-sm font-semibold text-ink">
                    {messageSettingsStrings.buttonLabelField}
                  </span>
                  <Input
                    value={buttonLabel}
                    maxLength={BUTTON_LABEL_MAX_LENGTH}
                    onChange={(e) => setButtonLabel(e.target.value)}
                    className="font-normal"
                  />
                  <span className="text-xs text-muted">
                    {messageSettingsStrings.buttonLabelHint}
                  </span>
                  <span className="text-right text-xs text-muted">
                    {buttonLabel.length}/{BUTTON_LABEL_MAX_LENGTH}
                  </span>
                  {!isButtonLabelValid ? (
                    <span className="text-xs text-red-600">
                      {trimmedButtonLabel.length === 0
                        ? messageSettingsStrings.buttonLabelRequired
                        : messageSettingsStrings.buttonLabelTooLong}
                    </span>
                  ) : null}
                </label>
              )}

              {sections.map((section, index) => (
                <ContentSection
                  key={section.id}
                  section={section}
                  index={index}
                  totalSections={sections.length}
                  variant={
                    activeTab === 'GREETING'
                      ? 'greetingMessageSettings'
                      : 'messageSettings'
                  }
                  assets={greetingCardAssets}
                  // PDFは挨拶を個別チャットにも送るときしか配信経路が無いため、その場合だけ選べるようにする
                  allowFileSection={allowFileSection}
                  maxVideoBytes={maxVideoBytes}
                  hideActionUrl
                  onChangeType={(type) => handleChangeType(section.id, type)}
                  onUpdateSection={(updates) =>
                    handleUpdateSection(section.id, updates)
                  }
                  onChangeContent={(content) =>
                    handlePatchSection(section.id, { content })
                  }
                  onChangeFile={(file) =>
                    handlePatchSection(section.id, { file })
                  }
                  onRemoveFile={() =>
                    handlePatchSection(section.id, { file: null })
                  }
                  onDelete={() => handleDeleteSection(section.id)}
                  onMove={(direction) =>
                    handleMoveSection(section.id, direction)
                  }
                  onSurveyUploadingChange={(uploading) =>
                    handleSurveyUploadingChange(section.id, uploading)
                  }
                />
              ))}

              {sections.length < MAX_SECTIONS && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={handleAddSection}
                    className="px-4 py-2 border border-accent rounded-lg hover:bg-accent-soft text-accent text-sm"
                  >
                    {messageSettingsStrings.addContent}
                  </button>
                </div>
              )}

              <div className="flex justify-end px-4 lg:-translate-y-[53px] ml-auto w-fit">
                <Button
                  onClick={() => save()}
                  disabled={
                    !hasRequiredSection ||
                    !isButtonLabelValid ||
                    isSaving ||
                    isSurveyImageUploading
                  }
                  loading={isSaving}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
                >
                  {messageSettingsStrings.save}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center lg:h-full w-full lg:w-[400px] lg:min-h-[700px] xl:-mt-[70px]">
            <div className="relative w-full max-w-[260px] sm:max-w-[300px] lg:max-w-[375px] mx-auto lg:mx-0 h-[460px] sm:h-[540px] lg:h-full lg:mr-2 mb-2 rounded-[3rem] border-8 border-white
             shadow-2xl flex flex-col overflow-hidden preview-phone-shadow z-10">
              <MessagePreview
                sections={sections}
                assets={greetingCardAssets}
                buttonLabel={trimmedButtonLabel || getDefaultButtonLabel(activeTab)}
              />
            </div>
          </div>
        </div>
      </section>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        variant={snackbar.variant}
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}
